import { useState } from "react";
import {
  Plus,
  Trash2,
  GripVertical,
  Type,
  Hash,
  Calendar,
  CheckSquare,
  List,
  FileText,
  ChevronDown,
  ChevronUp,
  Eye,
} from "lucide-react";

export const CustomFieldsBuilder = ({ fields, onChange, disabled }) => {
  const [expandedField, setExpandedField] = useState(null);

  const fieldTypes = [
    { value: "text", label: "Texto Corto", icon: Type },
    { value: "textarea", label: "Texto Largo", icon: FileText },
    { value: "number", label: "Número", icon: Hash },
    { value: "date", label: "Fecha", icon: Calendar },
    { value: "select", label: "Lista Desplegable", icon: List },
    { value: "checkbox", label: "Casilla de Verificación", icon: CheckSquare },
  ];

  const addField = () => {
    const newField = {
      id: `field_${Date.now()}`,
      name: `campo_${fields.length + 1}`,
      label: `Campo ${fields.length + 1}`,
      type: "text",
      required: false,
      placeholder: "",
      options: [], // Para select
      default_value: "",
    };
    onChange([...fields, newField]);
    setExpandedField(newField.id);
  };

  const removeField = (fieldId) => {
    onChange(fields.filter((f) => f.id !== fieldId));
    if (expandedField === fieldId) {
      setExpandedField(null);
    }
  };

  const updateField = (fieldId, updates) => {
    onChange(fields.map((f) => (f.id === fieldId ? { ...f, ...updates } : f)));
  };

  const moveField = (index, direction) => {
    const newFields = [...fields];
    const newIndex = direction === "up" ? index - 1 : index + 1;

    if (newIndex < 0 || newIndex >= newFields.length) return;

    [newFields[index], newFields[newIndex]] = [
      newFields[newIndex],
      newFields[index],
    ];
    onChange(newFields);
  };

  const addOption = (fieldId) => {
    const field = fields.find((f) => f.id === fieldId);
    if (!field) return;

    const newOptions = [
      ...(field.options || []),
      {
        value: `opcion_${(field.options?.length || 0) + 1}`,
        label: `Opción ${(field.options?.length || 0) + 1}`,
      },
    ];
    updateField(fieldId, { options: newOptions });
  };

  const updateOption = (fieldId, optionIndex, updates) => {
    const field = fields.find((f) => f.id === fieldId);
    if (!field) return;

    const newOptions = field.options.map((opt, idx) =>
      idx === optionIndex ? { ...opt, ...updates } : opt,
    );
    updateField(fieldId, { options: newOptions });
  };

  const removeOption = (fieldId, optionIndex) => {
    const field = fields.find((f) => f.id === fieldId);
    if (!field) return;

    const newOptions = field.options.filter((_, idx) => idx !== optionIndex);
    updateField(fieldId, { options: newOptions });
  };

  const getFieldIcon = (type) => {
    const fieldType = fieldTypes.find((ft) => ft.value === type);
    return fieldType ? fieldType.icon : Type;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-gray-900">
            Campos Personalizados
          </h3>
          <p className="text-sm text-gray-600">
            Agrega campos adicionales que se solicitarán al crear/renovar
            documentos
          </p>
        </div>
        {!disabled && (
          <button
            type="button"
            onClick={addField}
            className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 shadow-md transition-all flex items-center"
          >
            <Plus className="h-4 w-4 mr-2" />
            Agregar Campo
          </button>
        )}
      </div>

      {/* Lista de Campos */}
      {fields.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
          <FileText className="h-16 w-16 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">
            No hay campos personalizados
          </p>
          <p className="text-sm text-gray-400 mt-1">
            Haz clic en "Agregar Campo" para comenzar
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {fields.map((field, index) => {
            const Icon = getFieldIcon(field.type);
            const isExpanded = expandedField === field.id;

            return (
              <div
                key={field.id}
                className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden transition-all hover:border-indigo-300"
              >
                {/* Campo Header - Colapsable */}
                <div className="p-4 flex items-center justify-between bg-gray-50">
                  <div className="flex items-center flex-1">
                    {!disabled && (
                      <div className="flex flex-col mr-3">
                        <button
                          type="button"
                          onClick={() => moveField(index, "up")}
                          disabled={index === 0}
                          className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                        >
                          <ChevronUp className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveField(index, "down")}
                          disabled={index === fields.length - 1}
                          className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                        >
                          <ChevronDown className="h-4 w-4" />
                        </button>
                      </div>
                    )}

                    <GripVertical className="h-5 w-5 text-gray-400 mr-3" />
                    <Icon className="h-5 w-5 text-indigo-600 mr-3" />
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">
                        {field.label}
                      </p>
                      <p className="text-xs text-gray-500">
                        {field.name} •{" "}
                        {
                          fieldTypes.find((ft) => ft.value === field.type)
                            ?.label
                        }
                        {field.required && " • Obligatorio"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedField(isExpanded ? null : field.id)
                      }
                      className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      {isExpanded ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </button>
                    {!disabled && (
                      <button
                        type="button"
                        onClick={() => removeField(field.id)}
                        className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Campo Expandido - Configuración */}
                {isExpanded && (
                  <div className="p-4 border-t border-gray-200 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Nombre interno */}
                      <div>
                        <label htmlFor={`field_name_${field.id}`} className="block text-sm font-medium text-gray-700 mb-2">
                          Nombre Interno (sin espacios) *
                        </label>
                        <input
                          id={`field_name_${field.id}`}
                          type="text"
                          value={field.name}
                          onChange={(e) =>
                            updateField(field.id, {
                              name: e.target.value
                                .toLowerCase()
                                .replace(/\s/g, "_"),
                            })
                          }
                          disabled={disabled}
                          placeholder="numero_poliza"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                        />
                      </div>

                      {/* Etiqueta */}
                      <div>
                        <label htmlFor={`field_label_${field.id}`} className="block text-sm font-medium text-gray-700 mb-2">
                          Etiqueta (visible al usuario) *
                        </label>
                        <input
                          id={`field_label_${field.id}`}
                          type="text"
                          value={field.label}
                          onChange={(e) =>
                            updateField(field.id, { label: e.target.value })
                          }
                          disabled={disabled}
                          placeholder="Número de Póliza"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                        />
                      </div>

                      {/* Tipo de campo */}
                      <div>
                        <label htmlFor={`field_type_${field.id}`} className="block text-sm font-medium text-gray-700 mb-2">
                          Tipo de Campo *
                        </label>
                        <select
                          id={`field_type_${field.id}`}
                          value={field.type}
                          onChange={(e) =>
                            updateField(field.id, { type: e.target.value })
                          }
                          disabled={disabled}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                        >
                          {fieldTypes.map((ft) => (
                            <option key={ft.value} value={ft.value}>
                              {ft.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Placeholder */}
                      {(field.type === "text" ||
                        field.type === "textarea" ||
                        field.type === "number") && (
                          <div>
                            <label htmlFor={`field_placeholder_${field.id}`} className="block text-sm font-medium text-gray-700 mb-2">
                              Placeholder (opcional)
                            </label>
                            <input
                              id={`field_placeholder_${field.id}`}
                              type="text"
                              value={field.placeholder || ""}
                              onChange={(e) =>
                                updateField(field.id, {
                                  placeholder: e.target.value,
                                })
                              }
                              disabled={disabled}
                              placeholder="Ej: Ingrese el número..."
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                            />
                          </div>
                        )}
                    </div>

                    {/* Opciones para Select */}
                    {field.type === "select" && (
                      <div>
                        <span className="block text-sm font-medium text-gray-700 mb-2">
                          Opciones
                        </span>
                        <div className="space-y-2">
                          {(field.options || []).map((option, optIndex) => (
                            <div key={optIndex} className="flex gap-2">
                              <input
                                type="text"
                                value={option.value}
                                onChange={(e) =>
                                  updateOption(field.id, optIndex, {
                                    value: e.target.value,
                                  })
                                }
                                disabled={disabled}
                                placeholder="valor"
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                              />
                              <input
                                type="text"
                                value={option.label}
                                onChange={(e) =>
                                  updateOption(field.id, optIndex, {
                                    label: e.target.value,
                                  })
                                }
                                disabled={disabled}
                                placeholder="Etiqueta"
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                              />
                              {!disabled && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    removeOption(field.id, optIndex)
                                  }
                                  className="p-2 text-red-600 hover:bg-red-100 rounded-lg"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          ))}
                          {!disabled && (
                            <button
                              type="button"
                              onClick={() => addOption(field.id)}
                              className="w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-indigo-500 hover:text-indigo-600 transition-colors"
                            >
                              + Agregar Opción
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Valor por defecto */}
                    {field.type !== "checkbox" && (
                      <div>
                        <label htmlFor={`field_default_${field.id}`} className="block text-sm font-medium text-gray-700 mb-2">
                          Valor por Defecto (opcional)
                        </label>
                        {field.type === "select" ? (
                          <select
                            id={`field_default_${field.id}`}
                            value={field.default_value || ""}
                            onChange={(e) =>
                              updateField(field.id, {
                                default_value: e.target.value,
                              })
                            }
                            disabled={disabled}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                          >
                            <option value="">
                              -- Sin valor por defecto --
                            </option>
                            {(field.options || []).map((opt, idx) => (
                              <option key={idx} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            id={`field_default_${field.id}`}
                            type={
                              field.type === "number"
                                ? "number"
                                : field.type === "date"
                                  ? "date"
                                  : "text"
                            }
                            value={field.default_value || ""}
                            onChange={(e) =>
                              updateField(field.id, {
                                default_value: e.target.value,
                              })
                            }
                            disabled={disabled}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                          />
                        )}
                      </div>
                    )}

                    {/* Checkbox: Campo obligatorio */}
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={field.required}
                        onChange={(e) =>
                          updateField(field.id, { required: e.target.checked })
                        }
                        disabled={disabled}
                        className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      <span className="ml-2 text-sm font-medium text-gray-700">
                        Campo obligatorio
                      </span>
                    </label>

                    {/* Vista Previa del Campo */}
                    <div className="pt-4 border-t border-gray-200">
                      <div className="flex items-center mb-2">
                        <Eye className="h-4 w-4 text-gray-500 mr-2" />
                        <span className="text-sm font-medium text-gray-700">
                          Vista Previa
                        </span>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <label htmlFor={`preview_${field.id}`} className="block text-sm font-medium text-gray-700 mb-2">
                          {field.label}
                          {field.required && (
                            <span className="text-red-500 ml-1">*</span>
                          )}
                        </label>
                        {field.type === "text" && (
                          <input
                            id={`preview_${field.id}`}
                            type="text"
                            placeholder={field.placeholder || ""}
                            disabled
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                          />
                        )}
                        {field.type === "textarea" && (
                          <textarea
                            id={`preview_${field.id}`}
                            placeholder={field.placeholder || ""}
                            rows={3}
                            disabled
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                          />
                        )}
                        {field.type === "number" && (
                          <input
                            id={`preview_${field.id}`}
                            type="number"
                            placeholder={field.placeholder || ""}
                            disabled
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                          />
                        )}
                        {field.type === "date" && (
                          <input
                            id={`preview_${field.id}`}
                            type="date"
                            disabled
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                          />
                        )}
                        {field.type === "select" && (
                          <select
                            id={`preview_${field.id}`}
                            disabled
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                          >
                            <option>-- Seleccione --</option>
                            {(field.options || []).map((opt, idx) => (
                              <option key={idx} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        )}
                        {field.type === "checkbox" && (
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              disabled
                              className="w-4 h-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <span className="ml-2 text-sm text-gray-600">
                              {field.placeholder || "Activar opción"}
                            </span>
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
